import { useState } from 'react';
import { V1TradeExpirationType } from '@fintronners/react-api';
import { WebBodyText14, WebCaption10 } from '@fintronners/react-ui/src/GlobalStyling/webTypography';
import CurrencyInput from 'react-currency-input-field';
import { OrderType, QuantityType } from '@fintronners/internal-core/src/trade/constants';
import { Controller, useFormContext } from 'react-hook-form';
import { Radio } from '@/components/common/Radio/Radio';
import { GoodForDialog } from '@/components/features/trade/GoodForDialog/GoodForDialog';
import {
  apexSharesShortFormat,
  thousandFormatNumbroCurrency,
} from '@fintronners/react-utils/src/numberUtilsTSX';
import { TradeCurrency, TradeCurrencyError } from './TradeCurrency';
import { Button } from '@/components/common/Button';
import { StopLossDialog } from '@/components/features/trade/StopLossPriceDialog/StopLossPriceDialog';
import { TradeSide, TradeFormData } from '../schema';
import { useTranslation } from 'react-i18next';
import { TradeBreakdown } from '@/components/features/trade/TradeBreakdown/TradeBreakdown';
import type { TradeBreakdown as TradeBreakdownType } from '@/hooks/trade/useTradeBreakdown';

interface TradeStopLossFormProps {
  securitySymbol: string;
  tradeBreakdown: TradeBreakdownType;
  currentPrice: number;
  isFractionable?: boolean;
}

export const TradeStopLossForm = ({
  securitySymbol,
  tradeBreakdown,
  currentPrice,
  isFractionable,
}: TradeStopLossFormProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<TradeFormData>();
  const [showAvailableAtStopLossPrice, setShowAvailableAtStopLossPrice] = useState(false);

  const { shareBalance, shareAmount, shareBalanceInDollarsAtLimitPrice, limitPrice } =
    tradeBreakdown;

  const sharesToBeSold = shareAmount ? apexSharesShortFormat(shareAmount) : '-';
  const availableAtStopLossPrice = shareBalanceInDollarsAtLimitPrice
    ? thousandFormatNumbroCurrency(shareBalanceInDollarsAtLimitPrice)
    : '-';

  const expirationTypeLabels = {
    [V1TradeExpirationType.Day]: t('miscScreens.tradeOptions.durationOptionsGoodForOneDay'),
    [V1TradeExpirationType.GoodTilCancel]: t(
      'miscScreens.tradeOptions.durationOptionsGoodTillCancel',
    ),
  };

  const handleSellAllClick = () => {
    if (!isFractionable) {
      setValue('shares', shareBalance.toString());
      return;
    }

    setValue('quantityType', QuantityType.DOLLARS);
    setValue('amount', shareBalanceInDollarsAtLimitPrice.toString());
  };

  return (
    <div className="flex flex-col mb-8">
      <StopLossDialog
        sharesToBeSold={sharesToBeSold}
        securitySymbol={securitySymbol}
        availableAtStopLossPrice={availableAtStopLossPrice}
        stopLoss={thousandFormatNumbroCurrency(limitPrice)}
        open={showAvailableAtStopLossPrice}
        onOpenChange={setShowAvailableAtStopLossPrice}
      />
      <div>
        <WebBodyText14.Regular className="text-grey72">
          {t('miscScreens.tradeStopLoss.tradeStopLossEnterText')}
        </WebBodyText14.Regular>
        <Controller
          control={control}
          name="stopLoss"
          render={({ field: { value, onChange } }) => (
            <CurrencyInput
              className="mt-2 w-full border rounded h-[35px] px-3 py-2 text-sm text-darkestBlue focus-visible:outline-primary"
              prefix="$"
              value={value}
              placeholder="$0.00"
              decimalsLimit={2}
              allowNegativeValue={false}
              intlConfig={{ locale: 'en-US' }}
              onValueChange={(_value) => onChange(_value)}
            />
          )}
        />
        {limitPrice > currentPrice && (
          <div className="flex mt-2">
            <WebCaption10.Regular className="text-red">
              {t('miscScreens.tradeStopLoss.tradeStopLossSummaryLimitPriceTooHigh')}
            </WebCaption10.Regular>
          </div>
        )}
      </div>
      {limitPrice > 0 && (
        <>
          <div className="flex flex-col gap-2 mt-2">
            <div>
              <TradeCurrency
                side={TradeSide.SELL}
                showUnitToggler
                isFractionable={isFractionable}
              />

              <div className="mt-2">
                <Button variant="secondary" onClick={handleSellAllClick}>
                  {t('miscScreens.tradeDisplay.tradeOrderSellAll')}
                </Button>
              </div>
            </div>

            <TradeCurrencyError />

            <TradeBreakdown
              orderType={OrderType.STOP_LOSS}
              tradeBreakdown={tradeBreakdown}
              setShowAvailableAtStopLossPrice={setShowAvailableAtStopLossPrice}
            />
          </div>
          <div className="mt-8">
            <GoodForDialog />
            <ul className="flex gap-4 mt-2">
              {[V1TradeExpirationType.Day, V1TradeExpirationType.GoodTilCancel].map(
                (expirationType) => (
                  <li key={expirationType} className="w-full">
                    <Controller
                      name="duration"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <>
                          <Radio.Input
                            id={expirationType}
                            value={expirationType}
                            onChange={() => onChange?.(expirationType as V1TradeExpirationType)}
                            checked={expirationType === value}
                          />
                          <Radio.Label
                            className="capitalize"
                            variant="secondary"
                            htmlFor={expirationType.toString()}
                          >
                            {expirationTypeLabels[expirationType]}
                          </Radio.Label>
                        </>
                      )}
                    />
                  </li>
                ),
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};
